import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Sparkles,
  LayoutGrid,
  Activity as ActivityIcon,
  Inbox as InboxIcon,
  Users,
  ListChecks,
  CalendarDays,
  Settings as SettingsIcon,
  Menu,
  X,
  Search,
  LogOut,
  Send,
  Clock,
  Loader2,
  AlertTriangle,
  AlertOctagon,
  Handshake,
  MoreHorizontal,
  MoreVertical,
  RefreshCw,
  Download,
  Upload,
  Trash2,
  Edit3,
  Plus,
  Sun,
  Moon,
} from "lucide-react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAsk } from "@/context/AskContext";
import { CommandPalette } from "@/components/CommandPalette";
import ProjectsSidebar from "@/components/ProjectsSidebar";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";
import { relTime } from "@/lib/utils-fos";
import { Tip } from "@/components/ui/tooltip";
import PZeroLogo from "@/components/PZeroLogo";

// Primary navigation (top).
const NAV = [
  { to: "/", label: "Ask anything", icon: Sparkles, testid: "nav-ask", end: true },
  { to: "/attention", label: "Needs attention", icon: AlertOctagon, testid: "nav-attention" },
  { to: "/inbox", label: "Messages", icon: InboxIcon, testid: "nav-inbox" },
  { to: "/activity", label: "Activity", icon: ActivityIcon, testid: "nav-activity" },
  { to: "/projects", label: "Tasks", icon: LayoutGrid, testid: "nav-projects" },
  { to: "/commitments", label: "Commitments", icon: Handshake, testid: "nav-commitments" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/people", label: "People", icon: Users, testid: "nav-people" },
  { to: "/sops", label: "SOPs", icon: ListChecks, testid: "nav-sops" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout() {
  const [demoMode, setDemoMode] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dataMenuOpen, setDataMenuOpen] = useState(false);
  const [dataBusy, setDataBusy] = useState(false);
  const dataFileRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { threads, activeThreadId, queueMessage } = useAsk();
  const { theme, toggleTheme } = useTheme();
  const [quickInput, setQuickInput] = useState("");
  const [quickSending, setQuickSending] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const refreshStatus = async () => {
    try {
      const [settings, inbox] = await Promise.all([
        endpoints.settings(),
        endpoints.inbox ? endpoints.inbox({ status: "pending" }).catch(() => []) : Promise.resolve([]),
      ]);
      const dm = settings.find((s) => s.key === "demo_mode");
      setDemoMode(dm?.value === "true");
      setInboxCount((inbox || []).length);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    refreshStatus();
    const t = setInterval(refreshStatus, 30000);
    return () => clearInterval(t);
  }, []);

  // Uploaded demo CSVs are session-only: on a fresh app load, restore the
  // original demo seed if a csv override flag is still set.
  const csvRevertRan = useRef(false);
  useEffect(() => {
    if (csvRevertRan.current) return;
    csvRevertRan.current = true;
    const marker = sessionStorage.getItem("pz_boot_toast");
    if (marker) {
      sessionStorage.removeItem("pz_boot_toast");
      if (marker === "reverted") toast.info("Original data restored", { description: "Your uploaded CSV was session-only." });
      if (marker === "uploaded") toast.success("Custom data loaded", { description: "Session only — original data restores on refresh or logout." });
      if (marker === "reloaded") toast.success("Seed data reloaded");
      if (marker === "cleared") toast.success("Workspace cleared");
      if (marker === "uploaded") return;
    }
    (async () => {
      try {
        const settings = await endpoints.settings();
        const ov = settings.find((s) => s.key === "demo_csv_override");
        if (ov?.value === "true") {
          await endpoints.demoReset();
          sessionStorage.setItem("pz_boot_toast", "reverted");
          window.location.reload();
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const submitQuick = async (openThread) => {
    const text = quickInput.trim();
    if (!text || quickSending) return;
    setQuickSending(true);
    try {
      const t = await queueMessage(text);
      setQuickInput("");
      if (openThread) {
        navigate(`/?thread=${t.id}`);
      } else {
        toast.success("Chat queued", {
          description: "Running in the background — you'll see it in the Chats sidebar.",
          action: {
            label: "View",
            onClick: () => navigate(`/?thread=${t.id}`),
          },
          duration: 5000,
        });
      }
    } catch (e) {
      toast.error("Could not queue chat");
    } finally { setQuickSending(false); }
  };

  const runDataAction = async (fn, marker) => {
    setDataBusy(true);
    try {
      await fn();
      sessionStorage.setItem("pz_boot_toast", marker);
      window.location.reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Action failed");
      setDataBusy(false);
    }
  };
  const reloadData = () => {
    if (!window.confirm("Replace all current workspace data with the seed dataset?")) return;
    runDataAction(() => endpoints.demoReset(), "reloaded");
  };
  const clearData = () => {
    if (!window.confirm("Remove ALL data from your workspace? This cannot be undone.")) return;
    runDataAction(() => endpoints.demoClear(), "cleared");
  };
  const downloadDataCsv = async () => {
    try {
      const blob = await endpoints.demoExport();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "p0-demo-data.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Demo data downloaded as CSV");
    } catch { toast.error("Failed to export data"); }
  };
  const onDataUpload = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    runDataAction(() => endpoints.demoImport(f), "uploaded");
  };

  return (
    <div className="h-screen flex flex-col bg-[#FAFAFA] text-neutral-900 overflow-hidden">
      <header className="flex items-center gap-3 px-4 md:px-6 h-14 border-b border-neutral-200 sticky top-0 z-30 bg-white/95 backdrop-blur shrink-0">
        <div className="flex items-center gap-3 shrink-0">
          <button
            className="md:hidden p-2.5 rounded-md hover:bg-neutral-100 flex items-center justify-center"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex items-center gap-2">
            <PZeroLogo height={34} className="hidden sm:inline-flex" />
            <PZeroLogo height={30} iconOnly className="sm:hidden" />
          </div>
        </div>

        {/* Header quick Ask input — Enter queues in the background. */}
        <div className="flex-1 max-w-2xl mx-auto">
          <div className="group flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 bg-white focus-within:border-neutral-500 transition-colors">
            <Sparkles size={13} className="text-neutral-500 shrink-0" />
            <input
              value={quickInput}
              onChange={(e) => setQuickInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitQuick(e.shiftKey); // Enter = queue; Shift+Enter = queue and open
                }
              }}
              placeholder="Ask anything · press Enter to queue in background…"
              className="flex-1 min-w-0 text-sm bg-transparent outline-none placeholder-neutral-400"
              data-testid="header-ask-input"
              disabled={quickSending}
            />
            <Tip label="Queue (Enter): run in background, keep working here">
              <button
                onClick={() => submitQuick(false)}
                disabled={quickSending || !quickInput.trim()}
                className="p-1.5 rounded-md bg-neutral-900 text-white hover:bg-neutral-800 disabled:bg-neutral-200 disabled:text-neutral-500 transition-colors"
                data-testid="header-ask-queue"
              >
                {quickSending ? <Loader2 size={13} className="animate-spin" /> : <Clock size={13} />}
              </button>
            </Tip>
            <Tip label="Open (⇧Enter): queue and jump into the chat">
              <button
                onClick={() => submitQuick(true)}
                disabled={quickSending || !quickInput.trim()}
                className="p-1.5 rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 disabled:opacity-40 transition-colors"
                data-testid="header-ask-send"
              >
                {quickSending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              </button>
            </Tip>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Tip label="Search anything (⌘K)">
            <button
              data-testid="command-palette-btn"
              onClick={() => setPaletteOpen(true)}
              className="hidden lg:flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-md border border-neutral-200 text-neutral-600 hover:bg-neutral-100 transition-colors"
            >
              <Search size={12} />
              <span>Search</span>
              <kbd className="text-[10px] px-1 py-0.5 rounded bg-neutral-100 border border-neutral-200">⌘K</kbd>
            </button>
          </Tip>
          <div className="relative">
            <input ref={dataFileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onDataUpload} data-testid="user-data-upload-input" />
            <button
              data-testid="user-avatar"
              onClick={() => { setMenuOpen(!menuOpen); setDataMenuOpen(false); }}
              className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold overflow-hidden bg-neutral-200 text-neutral-800"
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-full w-full object-cover" />
              ) : (
                (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
              )}
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => { setMenuOpen(false); setDataMenuOpen(false); }} />
                <div data-testid="user-menu" className="absolute right-0 top-10 w-56 rounded-md border border-neutral-200 bg-white p-2 z-50 shadow-lg">
                  <div className="px-2 py-1.5 mb-1 border-b border-neutral-200 flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{user?.name}</p>
                      <p className="text-[11px] truncate text-neutral-500">{user?.email}</p>
                    </div>
                    <div className="relative shrink-0">
                      <button
                        data-testid="user-data-menu-btn"
                        aria-label="Data options"
                        onClick={() => setDataMenuOpen(!dataMenuOpen)}
                        className="p-1 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
                      >
                        <MoreVertical size={14} />
                      </button>
                      {dataMenuOpen && (
                        <div data-testid="user-data-menu" className="absolute right-0 top-7 w-64 rounded-md border border-neutral-200 bg-white p-1.5 z-50 shadow-lg">
                          <button data-testid="menu-reload-data" disabled={dataBusy} onClick={reloadData}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-neutral-100 disabled:opacity-50">
                            <RefreshCw size={14} className="text-neutral-500" /> Reload data
                          </button>
                          <button data-testid="menu-clear-data" disabled={dataBusy} onClick={clearData}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-neutral-100 text-red-600 disabled:opacity-50">
                            <Trash2 size={14} /> Clear data
                          </button>
                          <div className="border-t border-neutral-200 my-1" />
                          <button data-testid="menu-download-demo-csv" disabled={dataBusy} onClick={downloadDataCsv}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-neutral-100 disabled:opacity-50">
                            <Download size={14} className="text-neutral-500" /> Download demo data (CSV)
                          </button>
                          <button data-testid="menu-upload-demo-csv" disabled={dataBusy} onClick={() => dataFileRef.current?.click()}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-neutral-100 disabled:opacity-50">
                            <Upload size={14} className="text-neutral-500" /> Upload demo data (CSV)
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    data-testid="theme-toggle"
                    onClick={() => { toggleTheme(); toast.success(theme === "light" ? "Dark theme" : "Light theme"); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-neutral-100"
                  >
                    {theme === "light" ? <Moon size={14} /> : <Sun size={14} />}
                    <span className="flex-1 text-left">{theme === "light" ? "Dark theme" : "Light theme"}</span>
                    <span className="text-[10px] text-neutral-500 uppercase">{theme}</span>
                  </button>
                  <button
                    data-testid="logout-btn"
                    onClick={async () => { setMenuOpen(false); await logout(); navigate("/login"); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-neutral-100 text-red-600"
                  >
                    <LogOut size={14} /> Log out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <aside className="hidden md:flex md:w-60 lg:w-64 border-r border-neutral-200 flex-col shrink-0 bg-white min-h-0">
          {/* Primary nav */}
          <nav className="p-3 space-y-0.5 shrink-0">
            {NAV.map((n) => (
              <NavItem key={n.to} {...n} badge={n.to === "/inbox" ? inboxCount : 0} />
            ))}
          </nav>
          {/* Divider */}
          <div className="border-t border-neutral-200 mx-3" />
          {/* Secondary nav — context-aware */}
          <SecondaryNav
            route={location.pathname}
            threads={threads}
            activeThreadId={activeThreadId}
            onNavigate={(id) => navigate(id ? `/?thread=${id}` : "/")}
          />
          <div className="p-3 text-[10px] text-neutral-400 shrink-0 border-t border-neutral-200">v1.0 · claude-sonnet</div>
        </aside>

        {/* Mobile slide-in drawer + dark backdrop.
            Backdrop tap closes drawer. Nav item selection auto-closes via the
            location.pathname useEffect at the top of this component. */}
        <div
          className={`md:hidden fos-drawer-backdrop ${mobileOpen ? "open" : ""}`}
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-drawer-backdrop"
          aria-hidden={!mobileOpen}
        />
        <aside
          className={`md:hidden fos-drawer ${mobileOpen ? "open" : ""}`}
          data-testid="mobile-drawer"
          aria-hidden={!mobileOpen}
        >
          <div className="flex items-center justify-between p-3 border-b border-neutral-200 shrink-0">
            <PZeroLogo height={28} />
            <button
              className="p-2 rounded-md hover:bg-neutral-100"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-drawer-close"
            >
              <X size={18} />
            </button>
          </div>
          <nav className="p-3 space-y-0.5 shrink-0">
            {NAV.map((n) => (
              <NavItem key={n.to} {...n} badge={n.to === "/inbox" ? inboxCount : 0} />
            ))}
          </nav>
          <div className="border-t border-neutral-200 mx-3" />
          <SecondaryNav
            route={location.pathname}
            threads={threads}
            activeThreadId={activeThreadId}
            onNavigate={(id) => { navigate(id ? `/?thread=${id}` : "/"); setMobileOpen(false); }}
          />
        </aside>

        <main className="flex-1 min-w-0 min-h-0 overflow-hidden">
          <div className="h-full overflow-y-auto pb-16 md:pb-0">
            <Outlet context={{ demoMode, refreshStatus }} />
          </div>
        </main>
      </div>

      <div className="md:hidden fixed bottom-0 inset-x-0 border-t border-neutral-200 bg-white z-30 flex justify-around py-1.5">
        {NAV.slice(0, 6).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`mobile-${n.testid}`}
            className={({ isActive }) => `flex flex-col items-center p-1 text-[10px] ${isActive ? "text-neutral-900" : "text-neutral-500"}`}
          >
            <n.icon size={18} />
            <span className="mt-0.5">{n.label.split(" ")[0]}</span>
          </NavLink>
        ))}
      </div>

      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
    </div>
  );
}

function NavItem({ to, label, icon: Icon, testid, end, badge }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
          isActive ? "fos-selected" : "text-neutral-700 hover:bg-neutral-100"
        }`
      }
    >
      <Icon size={16} />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span
          data-testid={`badge-${testid}`}
          className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-[#FF453A] text-white"
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

// ============================================================================
// Secondary navigation — context-aware
//   /projects → project tree
//   otherwise → chat threads (Ask Anything)
// ============================================================================
function SecondaryNav({ route, threads, activeThreadId, onNavigate }) {
  if (route.startsWith("/projects")) return <ProjectsSidebar />;
  return <ChatsSidebar threads={threads} activeThreadId={activeThreadId} onNavigate={onNavigate} />;
}

function ChatsSidebar({ threads, activeThreadId, onNavigate }) {
  const { renameThread, deleteThread } = useAsk();
  const running = threads.filter((t) => t.status === "running").length;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-3 py-2 flex items-center justify-between shrink-0">
        <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">Chats {threads.length > 0 && `· ${threads.length}`}</span>
        <button
          onClick={() => onNavigate(null)}
          className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-900"
          data-testid="secondary-new-chat"
          title="New chat"
        >
          <Plus size={13} />
        </button>
      </div>
      {running > 0 && (
        <div className="mx-3 mb-2 flex items-center gap-1.5 text-[10px] text-neutral-600 bg-neutral-100 rounded px-2 py-1" data-testid="secondary-running-status">
          <Loader2 size={9} className="animate-spin" /> {running} chat{running === 1 ? "" : "s"} running
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5">
        {threads.length === 0 && (
          <p className="text-[11px] text-neutral-400 px-2 py-2">Start a chat from the top bar.</p>
        )}
        {threads.map((t) => (
          <ThreadItem
            key={t.id}
            thread={t}
            active={activeThreadId === t.id}
            onClick={() => onNavigate(t.id)}
            onRename={() => {
              const title = prompt("Rename chat", t.title);
              if (title !== null && title.trim()) renameThread(t.id, title.trim());
            }}
            onDelete={async () => {
              if (!confirm("Delete this chat?")) return;
              await deleteThread(t.id);
              if (activeThreadId === t.id) onNavigate(null);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function ThreadItem({ thread, active, onClick, onRename, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (!ref.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);
  const running = thread.status === "running";
  const errored = thread.status === "error";
  return (
    <div
      onClick={onClick}
      data-testid={`thread-item-${thread.id}`}
      className={`group flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm transition-colors ${
        active ? "fos-selected" : "text-neutral-700 hover:bg-neutral-100"
      }`}
    >
      <div className="w-3 shrink-0 flex justify-center">
        {running ? <Loader2 size={11} className="animate-spin text-neutral-500" data-testid={`thread-running-${thread.id}`} />
          : errored ? <AlertTriangle size={10} className="text-[#FF453A]" data-testid={`thread-error-${thread.id}`} />
          : <span className={`h-1 w-1 rounded-full ${active ? "bg-neutral-800" : "bg-transparent group-hover:bg-neutral-400"}`} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="truncate text-[13px]">{thread.title || "Untitled"}</p>
        {running && thread.current_step && (
          <p className="text-[10px] text-neutral-500 truncate">{thread.current_step}</p>
        )}
        {!running && !active && thread.last_snippet && (
          <p className="text-[10px] text-neutral-500 truncate">{thread.last_snippet}</p>
        )}
      </div>
      <div ref={ref} className="relative shrink-0 opacity-0 group-hover:opacity-100">
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
          className="p-1 rounded hover:bg-white text-neutral-500"
          data-testid={`thread-menu-${thread.id}`}
        >
          <MoreHorizontal size={12} />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 w-36 rounded-md border border-neutral-200 bg-white shadow-lg py-1 z-30" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setMenuOpen(false); onRename(); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-neutral-100" data-testid={`thread-rename-${thread.id}`}>
              <Edit3 size={11} /> Rename
            </button>
            <button onClick={() => { setMenuOpen(false); onDelete(); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-red-600 hover:bg-neutral-100" data-testid={`thread-delete-${thread.id}`}>
              <Trash2 size={11} /> Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
